import { type FC, type FormEvent, useState } from "react";
import { FiCreditCard, FiMail, FiMapPin, FiPackage, FiRefreshCw } from "react-icons/fi";
import { Link, useNavigate } from "react-router";
import { Button } from "../components/Button";
import { Container } from "../components/Container";
import { Field } from "../components/Field";
import { authApi } from "../rpc";

/**
 * マイページ未ログイン時の入口。
 *
 * 構成 (= Issue #74):
 *  1. Hero: サービス名 + 「ここで何ができるか」 の一行 catch
 *  2. 「マイページでできること」: 機能項目を icon + ヘッドライン + 補足の 3 段で並べる
 *     アイコンは react-icons/fi (Feather Icons) で統一。
 *  3. ログインフォーム: 既存のメールリンク (= magic link) 経路はそのまま
 *  4. フッタ: お問い合わせ先
 */
type Feature = {
  icon: typeof FiPackage;
  title: string;
  description: string;
};

const features: readonly Feature[] = [
  {
    icon: FiPackage,
    title: "ご注文の確認・配送状況の閲覧",
    description:
      "ご注文ごとのステータス、お届け予定時期、配送追跡番号をいつでもご確認いただけます。",
  },
  {
    icon: FiCreditCard,
    title: "お支払い情報の確認・お支払い方法の変更",
    description:
      "払込票や請求書がお手元になくても、マイページからいつでも再確認や別のお支払い方法へのお切り替えが行えます。",
  },
  {
    icon: FiMapPin,
    title: "お届け先の変更",
    description: "出荷作業に入る前であれば、お届け先住所をご自身で変更いただけます。",
  },
  {
    icon: FiRefreshCw,
    title: "不良品の交換 / 返品キャンペーン申請",
    description: "初期不良の交換やお試し返品キャンペーンのお申込みをマイページから直接行えます。",
  },
];

export const LoginPage: FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await authApi["request-magic-link"].$post({ json: { email: trimmed } });
      if (res.ok) {
        navigate(`/verify?email=${encodeURIComponent(trimmed)}`);
        return;
      }
      setError("送信に失敗しました。しばらくしてから再度お試しください。");
    } catch {
      setError("送信に失敗しました。ネットワーク接続をご確認ください。");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container>
      {/* Hero */}
      <header className="mb-10 space-y-2 text-center">
        <h1 className="font-medium text-3xl text-neutral-900 tracking-tight">
          サバキューのマイページ
        </h1>
        <p className="text-neutral-600 text-sm leading-relaxed">
          ご注文の確認から、お届け先の変更・返品申請まで、ここから一括で行えます。
        </p>
      </header>

      {/* Features */}
      <section className="mb-12">
        <h2 className="mb-4 font-medium text-base text-neutral-900">マイページでできること</h2>
        <ul className="space-y-4">
          {features.map((f) => (
            <li key={f.title} className="flex gap-3">
              <span
                aria-hidden
                className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-700"
              >
                <f.icon size={18} />
              </span>
              <div className="space-y-0.5">
                <p className="font-medium text-neutral-900 text-sm">{f.title}</p>
                <p className="text-neutral-500 text-xs leading-relaxed">{f.description}</p>
              </div>
            </li>
          ))}
        </ul>
      </section>

      {/* Login form */}
      <section className="rounded-lg border border-neutral-200 bg-neutral-50 p-5 sm:p-6">
        <h2 className="mb-1 flex items-center gap-2 font-medium text-base text-neutral-900">
          <FiMail size={18} className="text-neutral-500" />
          ログイン
        </h2>
        <p className="mb-4 text-neutral-500 text-xs leading-relaxed">
          ご注文時にご登録いただいたメールアドレス宛に、ログイン用のリンクをお送りします。
          パスワードは不要です。
        </p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Field
            label="メールアドレス"
            type="email"
            name="email"
            autoComplete="email"
            required
            placeholder="example@survaq.com"
            value={email}
            onChange={(e) => setEmail(e.currentTarget.value)}
            error={error ?? undefined}
          />
          <Button type="submit" loading={submitting} className="w-full">
            {submitting ? "送信しています…" : "ログインリンクを送る"}
          </Button>
        </form>
      </section>

      {/* Footer */}
      <footer className="mt-10 space-y-2 border-neutral-200 border-t pt-6 text-center text-neutral-500 text-xs leading-relaxed">
        <p>
          ご不明点は{" "}
          <Link to="/contact" className="underline underline-offset-4 hover:text-neutral-900">
            お問い合わせ先
          </Link>{" "}
          をご確認ください。
        </p>
        <p>
          Amazon・楽天市場・Yahoo! ショッピングでご購入の方は{" "}
          <Link
            to="/external-mall-contact"
            className="underline underline-offset-4 hover:text-neutral-900"
          >
            こちら
          </Link>
        </p>
      </footer>
    </Container>
  );
};
